import React, { useState, useEffect } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import MainButton from "../../button/MainButton";
import TextField from "../../fields/TextField";
import PhoneField from "../../fields/PhoneField";
import { useAuth } from "../../../contexts/AuthContext";
import { useGlobalInfo } from "../../../contexts/GlobalInfoContext";
import { updateUserInfo, setProfilePicture } from "../../../utilities/api/userService";
import { addTerms, getBrowserInfo, getCurrentUTCDateTime } from "../../../utilities/api/termsService";
import { getUserIP } from "../../../utilities/api/ipService";
import { enqueueSnackbar } from "notistack";
import AvatarUpload from "../AvatarUpload";
import { trimObjectValues } from "../../../utilities/helpers/formHelpers";
import { TickCircle } from "iconsax-react";
import TermsViewModal from "../../modal/TermsViewModal";

interface ProfileFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
}

const BasicInformation: React.FC = () => {
  const { accessToken } = useAuth();
  const { userInfo, refreshUserInfo } = useGlobalInfo();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAcceptingTerms, setIsAcceptingTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [initialData, setInitialData] = useState<ProfileFormData>({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    watch,
    reset,
  } = useForm<ProfileFormData>({
    mode: "onChange",
    defaultValues: initialData,
  });

  useEffect(() => {
    if (userInfo) {
      setInitialData({
        first_name: userInfo.first_name || "",
        last_name: userInfo.last_name || "",
        email: userInfo.email || "",
        phone_number: userInfo.phone_number || "",
      });
    }
  }, [userInfo]);

  useEffect(() => {
    reset(initialData);
  }, [initialData]);

  const onSubmit: SubmitHandler<ProfileFormData> = async (data) => {
    if (!accessToken) {
      enqueueSnackbar("Authentication failed. Please log in again.", { variant: "error" });
      return;
    }

    setIsSubmitting(true);
    try {
      const trimmedData = trimObjectValues(data);
      await updateUserInfo(accessToken, trimmedData);
      enqueueSnackbar("Profile updated successfully!", { variant: "success" });
      await refreshUserInfo();
    } catch (error: any) {
      console.error("Error updating profile:", error);
      enqueueSnackbar("Unable to update profile. Please try again.", { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAvatarUpload = async (base64Image: string) => {
    if (!accessToken) {
      enqueueSnackbar("Authentication failed. Please log in again.", { variant: "error" });
      return;
    }

    try {
      const response = await setProfilePicture(accessToken, base64Image);
      if (response.ok) {
        enqueueSnackbar("Profile picture updated successfully!", { variant: "success" });
        await refreshUserInfo();
      } else {
        throw new Error(response.message);
      }
    } catch (error: any) {
      console.error("Error updating profile picture:", error);
      enqueueSnackbar("Unable to update profile. Please try again. picture", { variant: "error" });
    }
  };

  // Check if should show terms acceptance button (personal info is filled)
  const shouldShowTermsButton = userInfo && userInfo.terms === false && userInfo.first_name && userInfo.email;

  // Check if should show terms banner with disabled button (personal info is not filled)
  const shouldShowTermsBannerDisabled = userInfo && userInfo.terms === false && (!userInfo.first_name || !userInfo.email);

  const handleAcceptTerms = async () => {
    if (!accessToken || !userInfo) {
      enqueueSnackbar("Authentication failed. Please log in again.", { variant: "error" });
      return;
    }

    setIsAcceptingTerms(true);
    try {
      const acceptedBy = `${userInfo.first_name || ""} ${userInfo.last_name || ""} – ${userInfo.role_info || "user"}`.trim();

      // Get user's real IP address
      const { ip: userIP } = await getUserIP();

      const params = {
        accepted_at: getCurrentUTCDateTime(),
        accepted_by: acceptedBy,
        acceptance_method: "Clickwrap",
        ip: userIP,
        browser: getBrowserInfo(),
        terms_version: "Terms & Conditions for Shaku Embedded SDK - 12/10/2025",
        tos_version: "ToS - 12/10/2025",
        privacy_version: "Privacy Policy for Shaku Embedded SDK - 12/10/2025",
        account_id: `${userInfo.id.toString()}`,
        status: "Active",
        comments: "",
      };

      const response = await addTerms(accessToken, params);
      if (response.ok) {
        enqueueSnackbar("Terms accepted successfully!", { variant: "success" });
        await refreshUserInfo();
      } else {
        enqueueSnackbar(response.message || "Unable to accept terms. Please try again.", { variant: "error" });
      }
    } catch (error: any) {
      console.error("Error accepting terms:", error);
      enqueueSnackbar("An error occurred while accepting terms. Please try again later.", { variant: "error" });
    } finally {
      setIsAcceptingTerms(false);
    }
  };

  return (
    <div className="flex flex-col gap-13 min-h-[40rem]">
      {shouldShowTermsButton && (
        <div className="flex flex-col items-center justify-between gap-4 p-4 border md:flex-row rounded-xl bg-gradient-to-r from-gorgonzola-blue-50/50 to-white border-gorgonzola-blue-100">
          <div className="flex flex-col items-center gap-4 md:flex-row">
            <div className="flex items-center justify-center flex-shrink-0 rounded-full size-12 bg-gorgonzola-blue-100">
              <TickCircle className="text-gorgonzola-blue-300 size-6" variant="Bold" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="mb-1 font-inter-bold text-closed-shutter-400">Accept Terms & Conditions</h3>
              <p className="text-xs text-closed-shutter-200">
                To complete your registration and start using Shaku services, please review and accept our{" "}
                <button type="button" onClick={() => setShowTermsModal(true)} className="font-inter-medium text-gorgonzola-blue-300 hover:underline">
                  Terms & Conditions
                </button>
                .
              </p>
            </div>
          </div>
          <div className="min-w-fit">
            <MainButton type="primary" onClick={handleAcceptTerms} disabled={isAcceptingTerms}>
              {isAcceptingTerms ? "Processing..." : "I Accept the Terms & Conditions"}
            </MainButton>
          </div>
        </div>
      )}

      {shouldShowTermsBannerDisabled && (
        <div className="flex items-center justify-between gap-4 p-4 border rounded-xl bg-gradient-to-r from-shindig-50/50 to-white border-shindig-100">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center flex-shrink-0 rounded-full size-12 bg-shindig-100">
              <TickCircle className="text-shindig-300 size-6" variant="Bold" />
            </div>
            <div className="flex-1">
              <h3 className="mb-1 font-inter-bold text-closed-shutter-400">Complete Personal Information First</h3>
              <p className="text-xs text-closed-shutter-200">
                Please fill in your personal information below before accepting the{" "}
                <button type="button" onClick={() => setShowTermsModal(true)} className="font-inter-medium text-gorgonzola-blue-300 hover:underline">
                  Terms & Conditions
                </button>
                .
              </p>
            </div>
          </div>
          <div className="min-w-fit">
            <MainButton type="secondary" disabled>
              I Accept the Terms & Conditions
            </MainButton>
          </div>
        </div>
      )}

      <div className="flex justify-center">
        <AvatarUpload onUpload={handleAvatarUpload} />
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-13">
          <Controller
            name="first_name"
            control={control}
            rules={{
              required: "First name is required",
              validate: {
                notEmpty: (value) => value.trim() !== "" || "First name cannot be empty",
              },
            }}
            render={({ field }) => <TextField label="First Name" placeholder="Enter your first name" {...field} error={!!errors.first_name} success={!!watch("first_name") && !errors.first_name} />}
          />
          <Controller
            name="last_name"
            control={control}
            rules={{
              required: "Last name is required",
              validate: {
                notEmpty: (value) => value.trim() !== "" || "Last name cannot be empty",
              },
            }}
            render={({ field }) => <TextField label="Last Name" placeholder="Enter your last name" {...field} error={!!errors.last_name} success={!!watch("last_name") && !errors.last_name} />}
          />
          <Controller
            name="email"
            control={control}
            rules={{
              required: "Email is required",
              pattern: {
                value: /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,})+$/,
                message: "Invalid email address",
              },
              validate: {
                notEmpty: (value) => value.trim() !== "" || "Email cannot be empty",
                noSpaces: (value) => !/\s/.test(value) || "Email cannot contain spaces",
              },
            }}
            render={({ field }) => <TextField label="Email" type="email" placeholder="YourEmail@gmail.com" {...field} error={!!errors.email} success={!!watch("email") && !errors.email} />}
          />
          <Controller
            name="phone_number"
            control={control}
            rules={{
              required: "Phone number is required",
              validate: {
                notEmpty: (value) => value.trim() !== "" || "Phone number cannot be empty",
              },
            }}
            render={({ field }) => (
              <PhoneField label="Phone Number" placeholder="Enter your phone number" {...field} error={!!errors.phone_number} success={!!watch("phone_number") && !errors.phone_number} />
            )}
          />
        </div>
        <div className="flex justify-end w-full">
          <MainButton type="submit" isSubmit disabled={!isValid || isSubmitting}>
            {isSubmitting ? "Updating..." : "Save Changes"}
          </MainButton>
        </div>
      </form>

      <TermsViewModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />
    </div>
  );
};

export default BasicInformation;
